import { Logout, Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DropdownTrigger, EasterEgg, LanguagePicker } from "@/components/common";
import { NAVBAR_HEIGHT, SIDEBAR_WIDTH } from "@/components/layout/constants";
import { Sidebar } from "@/components/layout/Sidebar/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useStoreBranding } from "@/hooks/useStoreBranding";
import { getRoleDisplayName } from "@/utils/roleManager";

interface NavbarProps {
  className?: string;
}

interface ActionItem {
  key: string;
  element: React.ReactElement;
}

const selectorMenuPaperSx = {
  mt: 1,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: 8,
};

export const Navbar: React.FC<NavbarProps> = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const hasDesktopHeader = useMediaQuery(theme.breakpoints.up("md"));
  const hasPersistentSidebar = useMediaQuery(theme.breakpoints.up("xl"));
  const usesDrawerNavigation = !hasPersistentSidebar;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { getStoreBranding, isLoading: isCustomerAvatarLoading } = useStoreBranding(
    user?.customerId,
  );
  const customerBranding = getStoreBranding(user?.customerId);

  const handleMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
      return;
    }

    setLanguageAnchorEl(null);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (languageAnchorEl) {
      setLanguageAnchorEl(null);
      return;
    }

    setAnchorEl(null);
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleMobileItemSelect = () => {
    // Close the mobile drawer after selecting a sidebar item
    handleMobileDrawerClose();
  };

  useEffect(() => {
    if (hasPersistentSidebar && mobileOpen) {
      handleMobileDrawerClose();
    }
  }, [hasPersistentSidebar, mobileOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {}
    handleMenuClose();
  };

  const createActionItems = (): ActionItem[] => {
    const items: (ActionItem | null)[] = [
      {
        key: "language-picker",
        element: (
          <LanguagePicker
            compact={!hasDesktopHeader}
            anchorEl={languageAnchorEl}
            onClose={handleLanguageMenuClose}
            onToggle={handleLanguageMenuToggle}
          />
        ),
      },
      {
        key: "profile",
        element: (
          <DropdownTrigger
            isOpen={Boolean(anchorEl)}
            menuId="account-menu"
            aria-label={t("common.user")}
            onClick={handleMenuToggle}
            sx={{
              gap: hasDesktopHeader ? 0.5 : 0,
              "& > :first-child": { marginInlineEnd: hasDesktopHeader ? 0.75 : 0 },
              ...(!hasDesktopHeader && {
                px: 0.5,
                "& .MuiButton-endIcon .MuiSvgIcon-root": { fontSize: 20 },
              }),
            }}
          >
            <>
              {isCustomerAvatarLoading ? (
                <Skeleton
                  aria-hidden="true"
                  variant="circular"
                  width={28}
                  height={28}
                  sx={{ flexShrink: 0, marginInlineEnd: 0.5 }}
                />
              ) : (
                <Avatar
                  src={customerBranding.avatar}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: customerBranding.color,
                    border: `1px solid ${customerBranding.color}`,
                    color: "common.white",
                    marginInlineEnd: 0.5,
                    fontSize: "14px",
                    fontWeight: 600,
                    "& .MuiAvatar-img": {
                      objectFit: "contain",
                    },
                  }}
                >
                  {user?.firstName?.charAt(0) || user?.email?.charAt(0) || "U"}
                </Avatar>
              )}
              {hasDesktopHeader && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0, textAlign: "start" }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      lineHeight: 1.2,
                      fontSize: "13px",
                      color: "text.primary",
                    }}
                  >
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.firstName || t("common.user")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: 1.2,
                      color: "text.secondary",
                      fontSize: "11px",
                      fontWeight: 500,
                    }}
                  >
                    {user?.role
                      ? getRoleDisplayName(user.role)
                      : t("layout.navbar.navbar.text.storeManager")}
                  </Typography>
                </Box>
              )}
            </>
          </DropdownTrigger>
        ),
      },
    ];

    return items.filter((item): item is ActionItem => item !== null);
  };

  const actionItems = createActionItems();

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          paddingTop: "env(safe-area-inset-top)",
          backgroundImage: isRtl
            ? "/* @noflip */ linear-gradient(225deg, rgba(79, 70, 229, 0.05) 0%, rgba(0, 102, 255, 0.05) 100%)"
            : "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(0, 102, 255, 0.05) 100%)",
          backgroundColor: mobileOpen ? "background.paper" : "rgba(255, 255, 255, 0.75)",
          backdropFilter: "blur(6em)",
          borderBottom: "1px solid rgba(79, 70, 229, 0.15)",
          boxShadow: "0 4px 15px rgba(79, 70, 229, 0.06)",
          color: "text.primary",
          borderRadius: "0",
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Toolbar
          sx={{
            minHeight: NAVBAR_HEIGHT,
            px: { xs: 1, sm: 2 },
            display: "flex",
            justifyContent: "flex-start",
            position: "relative",
            flexDirection: "row",
            gap: { xs: 0.25, sm: 1 },
          }}
        >
          {/* Mobile Menu Button */}
          {usesDrawerNavigation && (
            <IconButton
              color="inherit"
              aria-label={t("layout.navbar.navbar.accessibility.openDrawer")}
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{
                marginInlineEnd: 0,
                marginInlineStart: 0,
                backgroundColor: mobileOpen ? "rgba(15, 23, 42, 0.06)" : "transparent",
                borderRadius: "50%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: mobileOpen ? "rgba(15, 23, 42, 0.06)" : "rgba(15, 23, 42, 0.04)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box
            sx={{
              width: { xs: 170, sm: 178, md: 185 },
              minWidth: 0,
              flexShrink: 1,
              "& img": { display: "block", width: "100%", height: "auto", maxHeight: 36 },
            }}
          >
            <EasterEgg triggerOnDrag={true} triggerOnClick={false}>
              <img
                src="/images/crave_horizontal.svg"
                alt={t("layout.navbar.navbar.accessibility.craveRetail")}
              />
            </EasterEgg>
          </Box>

          {/* Actions */}
          <Stack
            direction="row"
            spacing={{ xs: 0.25, sm: 1, lg: 2 }}
            sx={{
              marginInlineStart: "auto",
              marginInlineEnd: 0,
              alignItems: "center",
              flexShrink: 0,
              "& .MuiIconButton-root:not(.dropdown-trigger__arrow)": {
                color: "text.secondary",
                borderRadius: "12px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "rgba(15, 23, 42, 0.04)",
                },
              },
            }}
          >
            {actionItems.map((item, index) => (
              <React.Fragment key={item.key}>
                {hasDesktopHeader && index > 0 && (
                  <Divider
                    orientation="vertical"
                    sx={{ alignSelf: "center", borderColor: "rgba(0, 0, 0, 0.23)", height: 24 }}
                  />
                )}
                {item.element}
              </React.Fragment>
            ))}
          </Stack>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{
              horizontal: isRtl ? "left" : "right",
              vertical: "top",
            }}
            anchorOrigin={{
              horizontal: isRtl ? "left" : "right",
              vertical: "bottom",
            }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  ...selectorMenuPaperSx,
                  minWidth: 200,
                  maxWidth: "calc(100vw - 32px)",
                  "& .MuiListItemIcon-root": {
                    minWidth: 36,
                  },
                },
              },
            }}
          >
            <ListSubheader
              disableSticky
              role="presentation"
              sx={{ px: 2, pt: 1, pb: 2, lineHeight: 1.4, overflowWrap: "anywhere" }}
            >
              <Typography variant="body2" fontWeight={600}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role
                  ? getRoleDisplayName(user.role)
                  : t("layout.navbar.navbar.text.storeManager")}
              </Typography>
            </ListSubheader>
            <Divider sx={{ mb: 1 }} />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ color: "text.primary" }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("common.signOut")}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.primary",
                  sx: { fontWeight: 500 },
                }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", xl: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            top: `calc(${NAVBAR_HEIGHT}px + env(safe-area-inset-top))`,
            height: `calc(100dvh - ${NAVBAR_HEIGHT}px - env(safe-area-inset-top))`,
            boxSizing: "border-box",
            background:
              "/* @noflip */ linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.98) 100%)",
            backdropFilter: "blur(20px)",
            borderRight: "1px solid rgba(79, 70, 229, 0.2)",
            boxShadow: "0 8px 32px rgba(79, 70, 229, 0.15)",
            borderRadius: "0 0 12px 0",
          },
          "& .MuiModal-backdrop": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <Sidebar isMobile={true} onItemSelect={handleMobileItemSelect} />
      </Drawer>
    </>
  );
};
